import { disablePageScroll, enablePageScroll } from 'scroll-lock'
import { focusable } from '@shopify/theme-a11y'

// Set active nav state from current path
const setActiveNavPaths = () => {
    const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '')
    const pathWithQuery = path + window.location.search
    const subSections = ['products/sports']

    document.querySelectorAll('[data-nav-path]').forEach(el => {
        const navPath = el.dataset.navPath
        let isActive = false

        if (el.hasAttribute('data-nav-exact')) {
            // Customer resource links — exact match on path, including query string when present
            isActive = navPath.includes('?') ? pathWithQuery === navPath : path === navPath
        } else if (navPath === 'products') {
            // "Products" is active for /products and product categories, but not the By Sport index.
            isActive = (path === 'products' || path.startsWith('products/')) && !subSections.includes(path)
        } else if (subSections.includes(navPath)) {
            // By Sport index — exact match only.
            isActive = path === navPath
        } else {
            // Resources, account — prefix match; contact, why-choose — exact match
            isActive = path === navPath || path.startsWith(navPath + '/')
        }

        el.classList.toggle('is-active', isActive)
    })
}

setActiveNavPaths()
document.body.addEventListener('htmx:afterSwap', setActiveNavPaths)

const mainNav = document.querySelector('.primary-nav-list')
const mobileNav = document.querySelector('.mobile-nav-overlay')
const mobileNavDrawer = mobileNav ? mobileNav.querySelector('[data-mobile-nav-drawer]') : null
const mobileNavScrollTarget = mobileNavDrawer || mobileNav
const navToggle = document.querySelector('.hamburger')
let desktopNavEscapeHandlerAttached = false
let mobilePanelAnimating = false
const MOBILE_NAV_TRANSITION_MS = 320

const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const waitForTransition = (element, fallbackMs = MOBILE_NAV_TRANSITION_MS) =>
    new Promise(resolve => {
        if (!element || prefersReducedMotion()) {
            resolve()
            return
        }

        let settled = false
        const finish = () => {
            if (settled) return
            settled = true
            element.removeEventListener('transitionend', onEnd)
            resolve()
        }
        const onEnd = event => {
            if (event.target !== element) return
            finish()
        }

        element.addEventListener('transitionend', onEnd)
        window.setTimeout(finish, fallbackMs + 40)
    })

const getMobilePanel = panelName =>
    mobileNav ? mobileNav.querySelector(`[data-mobile-panel="${panelName}"]`) : null

const syncMobilePanelAria = () => {
    if (!mobileNav) return

    mobileNav.querySelectorAll('[data-mobile-panel]').forEach(panel => {
        const isActive = panel.classList.contains('is-active')
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true')
    })
}

const focusMobilePanel = panel => {
    if (!panel) return

    // Prefer the panel chrome (close/back) so browse items do not keep a
    // sticky highlight after panel changes.
    const preferred =
        panel.querySelector('[data-mobile-menu-close], .mobile-nav-back') ||
        focusable(panel)[0]

    if (preferred) preferred.focus({ preventScroll: true })
}

const setActiveMobilePanel = async (panelName, direction = 'forward') => {
    if (!mobileNav || !panelName || mobilePanelAnimating) return

    const nextPanel = getMobilePanel(panelName)
    const currentPanel = mobileNav.querySelector('[data-mobile-panel].is-active')
    if (!nextPanel || nextPanel === currentPanel) return

    mobilePanelAnimating = true

    const reduceMotion = prefersReducedMotion()
    const enterClass = direction === 'back' ? 'is-enter-back' : 'is-enter-forward'
    const leaveClass = direction === 'back' ? 'is-leave-back' : 'is-leave-forward'

    nextPanel.classList.remove('is-enter-forward', 'is-enter-back', 'is-leave-forward', 'is-leave-back')
    if (currentPanel) {
        currentPanel.classList.remove('is-enter-forward', 'is-enter-back', 'is-leave-forward', 'is-leave-back')
    }

    if (reduceMotion) {
        if (currentPanel) currentPanel.classList.remove('is-active')
        nextPanel.classList.add('is-active')
        syncMobilePanelAria()
        focusMobilePanel(nextPanel)
        mobilePanelAnimating = false
        return
    }

    nextPanel.classList.add(enterClass)
    // eslint-disable-next-line no-unused-expressions
    nextPanel.offsetWidth
    nextPanel.classList.add('is-active')
    nextPanel.classList.remove(enterClass)

    if (currentPanel) {
        currentPanel.classList.add(leaveClass)
    }

    await Promise.all([
        waitForTransition(nextPanel),
        currentPanel ? waitForTransition(currentPanel) : Promise.resolve(),
    ])

    if (currentPanel) {
        currentPanel.classList.remove('is-active', leaveClass)
    }

    syncMobilePanelAria()
    focusMobilePanel(nextPanel)
    mobilePanelAnimating = false
}

const resetMobilePanelsToMain = () => {
    if (!mobileNav) return

    mobileNav.querySelectorAll('[data-mobile-panel]').forEach(panel => {
        panel.classList.remove(
            'is-active',
            'is-enter-forward',
            'is-enter-back',
            'is-leave-forward',
            'is-leave-back'
        )
        if (panel.dataset.mobilePanel === 'main') {
            panel.classList.add('is-active')
        }
    })
    syncMobilePanelAria()
}

const setHamburgerState = isOpen => {
    if (!navToggle) return
    navToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    navToggle.setAttribute('aria-label', isOpen ? 'Close Main Menu' : 'Open Main Menu')
}

const openMobileNav = async () => {
    if (!mobileNav || !mobileNavDrawer) return

    resetMobilePanelsToMain()
    document.body.classList.add('nav-is-open')
    mobileNav.setAttribute('aria-hidden', 'false')
    setHamburgerState(true)
    disablePageScroll(mobileNavScrollTarget)

    if (!prefersReducedMotion()) {
        mobileNavDrawer.classList.add('is-enter')
        // eslint-disable-next-line no-unused-expressions
        mobileNavDrawer.offsetWidth
        mobileNavDrawer.classList.remove('is-enter')
        mobileNavDrawer.classList.add('is-open')
        await waitForTransition(mobileNavDrawer)
    } else {
        mobileNavDrawer.classList.add('is-open')
    }

    focusMobilePanel(getMobilePanel('main'))
}

const closeMobileNav = async () => {
    if (!mobileNav || !mobileNavDrawer) return
    if (!document.body.classList.contains('nav-is-open')) return

    if (!prefersReducedMotion()) {
        mobileNavDrawer.classList.remove('is-open')
        mobileNavDrawer.classList.add('is-leave')
        await waitForTransition(mobileNavDrawer)
        mobileNavDrawer.classList.remove('is-leave')
    } else {
        mobileNavDrawer.classList.remove('is-open')
    }

    document.body.classList.remove('nav-is-open')
    mobileNav.setAttribute('aria-hidden', 'true')
    setHamburgerState(false)
    enablePageScroll(mobileNavScrollTarget)
    resetMobilePanelsToMain()
    if (navToggle) navToggle.focus()
}

const handleDesktopNavEscape = event => {
    if (event.key !== 'Escape') return

    const openItem = document.querySelector('[data-nav-item].is-open')
    if (!openItem) return

    event.preventDefault()
    closeDesktopNav(openItem, true)
}

const addDesktopNavEscapeHandler = () => {
    if (desktopNavEscapeHandlerAttached) return

    document.addEventListener('keydown', handleDesktopNavEscape)
    desktopNavEscapeHandlerAttached = true
}

const removeDesktopNavEscapeHandler = () => {
    if (!desktopNavEscapeHandlerAttached) return

    document.removeEventListener('keydown', handleDesktopNavEscape)
    desktopNavEscapeHandlerAttached = false
}

const focusTopLevelItem = item => {
    const trigger = item && item.querySelector('.primary-nav-link')
    if (trigger) trigger.focus()
}

const closeDesktopNav = (item, returnFocus = false) => {
    if (!item) return

    const toggle = item.querySelector('[data-nav-menu-toggle]')
    const flyout = item.querySelector('[data-nav-menu]')
    item.classList.remove('is-open')
    if (flyout) enablePageScroll(flyout)
    if (!document.querySelector('[data-nav-item].is-open')) removeDesktopNavEscapeHandler()
    if (toggle) {
        toggle.setAttribute('aria-expanded', 'false')
        if (returnFocus) toggle.focus()
    }
}

const closeAllDesktopNav = (exceptItem = null) => {
    document.querySelectorAll('[data-nav-item].is-open').forEach(item => {
        if (item !== exceptItem) closeDesktopNav(item)
    })
}

const openDesktopNav = item => {
    if (!item) return

    const toggle = item.querySelector('[data-nav-menu-toggle]')
    const flyout = item.querySelector('[data-nav-menu]')
    const header = document.querySelector('.site-header')
    closeAllDesktopNav(item)
    resetActiveNavGroup(item)

    if (flyout && header) {
        const headerBottom = Math.max(0, header.getBoundingClientRect().bottom)
        flyout.style.top = `${headerBottom}px`
        flyout.style.height = `calc(100vh - ${headerBottom}px)`
        disablePageScroll(flyout)
    }

    item.classList.add('is-open')
    addDesktopNavEscapeHandler()
    if (toggle) toggle.setAttribute('aria-expanded', 'true')
}

const resetActiveNavGroup = item => {
    const flyout = item && item.querySelector('[data-nav-menu]')
    if (!flyout) return

    flyout.classList.remove('has-active-group')
    flyout.querySelectorAll('[data-nav-group-toggle]').forEach(groupToggle => {
        groupToggle.classList.remove('is-active')
        groupToggle.setAttribute('aria-expanded', 'false')
    })
    flyout.querySelectorAll('[data-nav-group-panel]').forEach(panel => {
        panel.classList.remove('is-active')
    })
}

const setActiveNavGroup = toggle => {
    if (!toggle) return

    const flyout = toggle.closest('[data-nav-menu]')
    const targetId = toggle.dataset.navGroupTarget
    if (!flyout || !targetId) return

    flyout.classList.add('has-active-group')
    flyout.querySelectorAll('[data-nav-group-toggle]').forEach(groupToggle => {
        const isActive = groupToggle === toggle
        groupToggle.classList.toggle('is-active', isActive)
        groupToggle.setAttribute('aria-expanded', isActive ? 'true' : 'false')
    })

    flyout.querySelectorAll('[data-nav-group-panel]').forEach(panel => {
        panel.classList.toggle('is-active', panel.id === targetId)
    })
}

if (mainNav) {
    const mainNavItems = [...mainNav.querySelectorAll('.primary-nav-item')]
    const [first] = mainNavItems
    const [last] = mainNavItems.reverse()
    const subnavs = document.querySelectorAll('.subnav')
    const desktopBrowseItems = [...mainNav.querySelectorAll('[data-nav-item]')]

    // Allow left/right arrow navigation of top level nav
    mainNav.addEventListener('keyup', (event) => {
        const currentFocus = document.activeElement;
        if (!currentFocus.classList.contains('primary-nav-link')) return

        if (event.key === 'ArrowRight') {
            if (currentFocus.parentElement.nextElementSibling) {
                focusTopLevelItem(currentFocus.parentElement.nextElementSibling)
                return
            }
            // At the end of the nav, focus the first element
            focusTopLevelItem(first)
        }

        if (event.key === 'ArrowLeft') {
            if (currentFocus.parentElement.previousElementSibling) {
                focusTopLevelItem(currentFocus.parentElement.previousElementSibling)
                return
            }

            // At the end of the nav, focus the first element
            focusTopLevelItem(last)
        }
    })

    desktopBrowseItems.forEach(item => {
        const toggle = item.querySelector('[data-nav-menu-toggle]')
        const groupToggles = [...item.querySelectorAll('[data-nav-group-toggle]')]

        if (toggle) {
            toggle.addEventListener('click', event => {
                event.preventDefault()

                if (item.classList.contains('is-open')) {
                    return
                }
                openDesktopNav(item)
            })

            toggle.addEventListener('keydown', event => {
                if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    openDesktopNav(item)
                    if (groupToggles.length) groupToggles[0].focus()
                }

                if (event.key === 'Escape') {
                    event.preventDefault()
                    closeDesktopNav(item, true)
                }
            })
        }

        groupToggles.forEach((groupToggle, index) => {
            groupToggle.addEventListener('click', () => setActiveNavGroup(groupToggle))

            groupToggle.addEventListener('keydown', event => {
                const lastIndex = groupToggles.length - 1

                if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    groupToggles[index === lastIndex ? 0 : index + 1].focus()
                }

                if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    groupToggles[index === 0 ? lastIndex : index - 1].focus()
                }

                if (event.key === 'Escape') {
                    event.preventDefault()
                    closeDesktopNav(item, true)
                }

                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setActiveNavGroup(groupToggle)
                }
            })
        })
    })

    document.addEventListener('click', event => {
        const clickIsInsideOpenMenu = desktopBrowseItems.some(item => {
            if (!item.classList.contains('is-open')) return false

            const toggle = item.querySelector('[data-nav-menu-toggle]')
            const flyout = item.querySelector('[data-nav-menu]')
            const groups = item.querySelector('.nav-flyout__groups')
            const highlights = item.querySelector('.nav-flyout__highlights')

            if (toggle && toggle.contains(event.target)) return true
            if (groups && groups.contains(event.target)) return true

            // Empty reserved highlights area should close the menu until a
            // category is selected; once open, keep clicks inside the panel.
            if (
                flyout &&
                flyout.classList.contains('has-active-group') &&
                highlights &&
                highlights.contains(event.target)
            ) {
                return true
            }

            return false
        })

        if (!clickIsInsideOpenMenu) closeAllDesktopNav()
    })

    // Jump out of subnav if escape is pressed inside
    subnavs.forEach(subnav => {
        subnav.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') {
                subnav.previousElementSibling.focus()
            }
        })
    })
}

//Hamburger Menu
if (navToggle) {
    navToggle.addEventListener('click', () => {
        if (document.body.classList.contains('nav-is-open')) {
            closeMobileNav()
            return
        }
        openMobileNav()
    })
}

if (mobileNav) {
    mobileNav.querySelectorAll('[data-mobile-menu-close]').forEach(closeButton => {
        closeButton.addEventListener('click', closeMobileNav)
    })

    mobileNav.querySelectorAll('[data-mobile-panel-open]').forEach(panelTrigger => {
        panelTrigger.addEventListener('click', () => {
            const direction = panelTrigger.dataset.mobileNavDirection === 'back' ? 'back' : 'forward'
            setActiveMobilePanel(panelTrigger.dataset.mobilePanelOpen, direction)
        })
    })

    mobileNav.addEventListener('click', event => {
        if (event.target === mobileNav) closeMobileNav()
    })

    document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && document.body.classList.contains('nav-is-open')) {
            closeMobileNav()
        }
    })
}
